import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { toast } from 'react-toastify';
import { FormikProps, FormikProvider, useFormik } from 'formik';

import { ISignUpDto } from 'interfaces/auth.interface';
import { useAuth } from 'contexts/auth.context';
import { signUp } from 'requests/auth.request';
import { removeAccessToken } from 'requests/token.request';
import { markInvalidFields } from 'utils/form.utils';
import { SIGN_UP_VALIDATION_SCHEMA } from 'constants/auth.constant';
import Loader from 'components/loader/loader.component';
import Input from 'components/input/input.component';
import Checkbox from 'components/checkbox/checkbox.component';
import Button from 'components/button/button.component';
import ConfirmDocModal from './modals/confirm-doc.modal';
import './auth.style.scss';

const SignUp = () => {
  const { t } = useTranslation();
  const { handleSetToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const formik: FormikProps<ISignUpDto> = useFormik({
    initialValues: {
      username: '',
      password: '',
      qoldauToken: '',
    },
    validationSchema: SIGN_UP_VALIDATION_SCHEMA(),
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      console.log(values);
    },
  });
  const [confirm, setConfirm] = useState<boolean>(false);
  const [docModal, setDocModal] = useState<
    'processingPersonalDataConsent' | 'userAgreement' | null
  >(null);

  useEffect(() => {
    removeAccessToken();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    formik.validateForm().then(() => {
      if (Object.keys(formik.errors).length > 0) {
        markInvalidFields(formik, 'auth');
      } else {
        setLoading(true);
        signUp(formik.values)
          .then((res) => handleSetToken(res.token))
          .catch(() => toast.error('Произошла ошибка при регистрации'))
          .finally(() => setLoading(false));
      }
    });
  };

  const handleChange = ({ target: { name, value } }: React.ChangeEvent<HTMLInputElement>) => {
    void formik.setFieldValue(name, value, true);
  };

  const handleChangeCheckbox = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    setConfirm(target.checked);
  };

  const handleDocModal =
    (docType: 'processingPersonalDataConsent' | 'userAgreement') => (event: React.MouseEvent) => {
      event.preventDefault();
      setDocModal(docType);
    };

  return (
    <Loader loading={loading}>
      <div className="auth__container">
        <div className="auth__box">
          <h2>{t('auth.signUp')}</h2>
          <FormikProvider value={formik}>
            <form onSubmit={handleSubmit} noValidate={true}>
              <Input
                formik={formik}
                name="username"
                value={formik.values.username}
                label={t('auth.iinBin')}
                onChange={handleChange}
              />
              <Input
                formik={formik}
                type="password"
                name="password"
                value={formik.values.password}
                label={t('auth.password')}
                onChange={handleChange}
              />
              <Input
                formik={formik}
                name="qoldauToken"
                value={formik.values.qoldauToken}
                label={t('auth.qoldauToken')}
                onChange={handleChange}
              />
              <div className="auth__confirm">
                <Checkbox name="confirm" checked={confirm} onChange={handleChangeCheckbox}>
                  <Trans i18nKey="policy.confirm">
                    <span onClick={handleDocModal('processingPersonalDataConsent')}>
                      согласие на обработку персональных данных
                    </span>
                    <span onClick={handleDocModal('userAgreement')}>
                      пользовательского соглашения
                    </span>
                  </Trans>
                </Checkbox>
              </div>
              <Button type="submit" primary disabled={!confirm}>
                {t('action.signUp')}
              </Button>
              <div className="auth__footer">
                <span>{t('auth.haveAccount')}</span>
                <Link to={'/sign-in'}>{t('action.signIn')}</Link>
              </div>
            </form>
          </FormikProvider>
        </div>
      </div>

      {!!docModal && <ConfirmDocModal docType={docModal} handleClose={() => setDocModal(null)} />}
    </Loader>
  );
};

export default SignUp;
